import React, { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Hero from '../components/home/Hero';
import useSiteData from '../hooks/useSiteData';
import HomeSkeleton from '../components/skeletons/HomeSkeleton';
import ErrorBoundary from '../components/ErrorBoundary';

// Lazy load components
const Services = React.lazy(() => import('../components/home/Services'));
const Stats = React.lazy(() => import('../components/home/Stats'));
const Reviews = React.lazy(() => import('../components/home/Reviews'));
const Gallery = React.lazy(() => import('../components/home/Gallery'));

const Home = () => {
  const { data, isLoading, error } = useSiteData();
  const location = useLocation();
  const currentUrl = window.location.origin + location.pathname;

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash, isLoading]);

  const contentPlaceholder = {
    minHeight: {
      hero: '600px',
      services: '400px',
      stats: '300px',
      reviews: '400px',
      gallery: '500px'
    }
  };

  // Updated SEO Data with your business info
  const seoData = {
    title: "Masala Cottage - Authentic Indian Restaurant in McDonough, GA",
    description: "Experience authentic Indian cuisine at Masala Cottage in McDonough, GA. Dine-in, takeaway, and catering services available. Open Monday through Saturday, 11:30 AM - 8:30 PM.",
    keywords: "Indian restaurant, McDonough GA, Indian food, curry, masala, dining, takeaway, catering, 931 Hwy 155 S, authentic Indian cuisine, halal",
    image: data?.hero?.backgroundImage || '/logo.png',
    businessInfo: {
      name: "Masala Cottage",
      phone: "+1-678-712-5129",
      email: "contact@masalacottage.com",
      address: "931 Hwy 155 S, McDonough, GA 30253"
    }
  };

  // Updated Structured Data with your business info
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": "Masala Cottage",
    "image": seoData.image,
    "url": currentUrl,
    "@id": currentUrl,
    "description": seoData.description,
    "hasMenu": `${currentUrl}#services`,
    "servesCuisine": "Indian",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "931 Hwy 155 S",
      "addressLocality": "McDonough",
      "addressRegion": "GA",
      "postalCode": "30253",
      "addressCountry": "US"
    },
    "telephone": "+1-678-712-5129",
    "email": "contact@masalacottage.com",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "11:30",
        "closes": "20:30"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "00:00",
        "closes": "00:00"
      }
    ],
    "menu": `${currentUrl}menu`,
    "acceptsReservations": "No"
  };

  // Navigation sections
  const sections = [
    { id: 'services', title: 'Services' },
    { id: 'stats', title: 'Stats' },
    { id: 'reviews', title: 'Reviews' },
    { id: 'gallery', title: 'Gallery' }
  ];

  if (isLoading) return <HomeSkeleton heights={contentPlaceholder} />;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;
  if (!data) return <div className="p-4 text-red-500">Error: No data available</div>;

  return (
    <HelmetProvider>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />

        {/* Location Meta Tags */}
        <meta name="geo.region" content="US-GA" />
        <meta name="geo.placename" content="McDonough" />
        <meta name="geo.position" content="33.4251;-84.1496" />
        <meta name="ICBM" content="33.4251, -84.1496" />

        {/* Business Meta Tags */}
        <meta name="business:contact_data:street_address" content="931 Hwy 155 S" />
        <meta name="business:contact_data:locality" content="McDonough" />
        <meta name="business:contact_data:region" content="GA" />
        <meta name="business:contact_data:postal_code" content="30253" />
        <meta name="business:contact_data:country_name" content="USA" />
        <meta name="business:contact_data:email" content="contact@masalacottage.com" />
        <meta name="business:contact_data:phone_number" content="+1-678-712-5129" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="restaurant.restaurant" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="restaurant:contact_info:street_address" content="931 Hwy 155 S" />
        <meta property="restaurant:contact_info:locality" content="McDonough" />
        <meta property="restaurant:contact_info:region" content="GA" />
        <meta property="restaurant:contact_info:postal_code" content="30253" />
        <meta property="restaurant:contact_info:country_name" content="USA" />
        <meta property="restaurant:contact_info:email" content="contact@masalacottage.com" />
        <meta property="restaurant:contact_info:phone_number" content="+1-678-712-5129" />
        <meta property="restaurant:contact_info:website" content={currentUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content={seoData.image} />

        {/* Canonical URL */}
        <link rel="canonical" href={currentUrl} />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="Masala Cottage" />
      </Helmet>

      <div className="min-h-screen">
        {/* Quick Navigation
        <nav className="sticky top-0 bg-white/80 backdrop-blur-sm z-50 shadow-sm" aria-label="Page sections">
          <div className="container mx-auto px-4">
            <ul className="flex justify-center space-x-6 py-3">
              {sections.map(section => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-gray-600 hover:text-primary transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(`#${section.id}`)
                        .scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav> */}

        {/* Main Content */}
        <main>
          <ErrorBoundary>
            <div style={{ minHeight: contentPlaceholder.minHeight.hero }}>
              {data.hero ? (
                <Hero heroData={data.hero} />
              ) : (
                <div className="p-4 text-yellow-500">Hero section data not available</div>
              )}
            </div>
          </ErrorBoundary>

          <Suspense fallback={<div style={{ minHeight: contentPlaceholder.minHeight.services }} />}>
            <ErrorBoundary>
              <section id="services" className="scroll-mt-16">
                <Services />
              </section>
            </ErrorBoundary>
          </Suspense>

          <Suspense fallback={<div style={{ minHeight: contentPlaceholder.minHeight.stats }} />}>
            <ErrorBoundary>
              <section id="stats" className="scroll-mt-16">
                {data.stats ? (
                  <Stats statsData={data.stats} />
                ) : (
                  <div className="p-4 text-yellow-500">Stats section data not available</div>
                )}
              </section>
            </ErrorBoundary>
          </Suspense>

          <Suspense fallback={<div style={{ minHeight: contentPlaceholder.minHeight.reviews }} />}>
            <ErrorBoundary>
              <section id="reviews" className="scroll-mt-16">
                {data.reviews ? (
                  <Reviews reviewsData={data.reviews} />
                ) : (
                  <div className="p-4 text-yellow-500">Reviews section data not available</div>
                )}
              </section>
            </ErrorBoundary>
          </Suspense>

          <Suspense fallback={<div style={{ minHeight: contentPlaceholder.minHeight.gallery }} />}>
            <ErrorBoundary>
              <section id="gallery" className="scroll-mt-16">
                {data.gallery ? (
                  <Gallery galleryData={data.gallery} />
                ) : (
                  <div className="p-4 text-yellow-500">Gallery section data not available</div>
                )}
              </section>
            </ErrorBoundary>
          </Suspense>
        </main>
      </div>
    </HelmetProvider>
  );
};

export default Home;